import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { API } from './constants';
import {
  MovementsStatisticsResponse,
  MovementHistoryFilters,
  MovementHistoryResponse,
  StatisticsType
} from '../models/movements';
import { TransactionNote } from '../store/types';

export interface ExportMovementsRequest {
  format?: 'pdf' | 'excel';
  filename?: string;
  startDate?: string;
  endDate?: string;
}

@Injectable({ providedIn: 'root' })
export class MovementsService extends ApiService {
  getMovementHistory(accountNumber: string, filters: MovementHistoryFilters) {
    return this.post<MovementHistoryResponse>(`${API.getMovementHistory}/${accountNumber}`, { body: filters });
  }

  /**
   * Obtiene las estadísticas (agregadas en el backend) de una cuenta para el rango
   * indicado. Según `type` devuelve los ingresos ('income') o los gastos
   * ('expense'). Devuelve la serie diaria y los totales ya calculados.
   */
  getStatistics(
    accountNumber: string,
    startDate: string,
    endDate: string,
    type: StatisticsType = 'income'
  ) {
    return this.post<MovementsStatisticsResponse>(`${API.getMovementsStatistics}/${accountNumber}`, {
      body: { startDate, endDate, type }
    });
  }

  /** @deprecated Usa `getStatistics`. Se mantiene como alias por compatibilidad. */
  getIncomeStatistics(
    accountNumber: string,
    startDate: string,
    endDate: string,
    type: StatisticsType = 'income'
  ) {
    return this.getStatistics(accountNumber, startDate, endDate, type);
  }

  exportMovements(accountNumber: string, exportRequest: ExportMovementsRequest = { format: 'pdf' }) {
    const endpoint = exportRequest.format === 'excel' 
      ? `${API.exportMovementsExcel}/${accountNumber}`
      : `${API.exportMovements}/${accountNumber}`;
    
    return this.post(endpoint, { 
      body: exportRequest,
      responseType: 'blob'
    });
  }

  // ─── Transaction Notes ───────────────────────────────────────────────────────

  getTransactionNotes(transactionCode: string) {
    return this.post<TransactionNote[]>(API.getTransactionNotes, {
      body: { transactionCode }
    });
  }

  getTransactionNotesCount(transactionCodes: string[]) {
    return this.post<Record<string, number>>(API.getTransactionNotesCount, {
      body: { transactionCodes }
    });
  }

  addTransactionNote(transactionCode: string, content: string) {
    return this.post<TransactionNote>(API.addTransactionNote, {
      body: { transactionCode, content }
    });
  }

  updateTransactionNote(noteId: string, content: string) {
    return this.patch<TransactionNote>(`${API.updateTransactionNote}/${noteId}`, {
      body: { content }
    });
  }

  deleteTransactionNote(noteId: string) {
    return this.delete(`${API.deleteTransactionNote}/${noteId}`);
  }
}
